<script setup lang="ts">
import LxcForm from "@lxc/app-device-common/src/components/form/LxcForm.vue";
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import type LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import { Filters } from "~/core/models/filters";
import CampaignOperationMgrFunnelStep1SelectFirmware from "~/modules/campaign/components/CampaignOperationMgrFunnelStep1SelectFirmware.vue";
import { getOperationModelTypeLabel } from "~/modules/campaign/utils/getOperationModelTypeLabel";
import { useDtwinOperationModels } from "~/modules/dtwin/composables/useDtwinOperationManager";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

export interface CampaignFunnelStep1 {
  name?: string;
  operationModelType?: OperationModelType;
  firmwareUuid?: string;
}

const emit = defineEmits([
  "allOperationModelsLoaded",
  "update:modelValue",
  "selectFirmware",
]);

const props = defineProps<{
  modelValue: CampaignFunnelStep1;
  canEdit: boolean;
  isEditing: boolean;
}>();

const { t, te } = useI18n();

const isLoadingAllOperationModels: Ref<boolean> = ref(false);
const errorAllOperationModels: Ref<LxcError | undefined> = ref(undefined);
const allOperationModels: Ref<OperationModel[]> = ref([]);
const { currentOrganization } = storeToRefs(useOrganizationStore());
const {
  error: dtwinOperationModelsError,
  fetchAllData: fetchAllDtwinOperationModels,
  allResults: dtwinOperationModelsResults,
  setFilter: setDtwinOperationModelsFilter,
} = useDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

const distinctOperationModelTypes: ComputedRef<OperationModelType[]> = computed(
  () => [
    ...new Set(
      allOperationModels.value.map((operationModel) => operationModel.type),
    ),
  ],
);

// set all the operation model list
async function setAllOperationModels() {
  errorAllOperationModels.value = undefined;
  isLoadingAllOperationModels.value = true;
  setDtwinOperationModelsFilter(Filters.DTWIN_OPERATION_MODEL_HIDDEN, "false");
  await fetchAllDtwinOperationModels();
  if (dtwinOperationModelsError.value) {
    errorAllOperationModels.value = dtwinOperationModelsError.value;
  } else {
    allOperationModels.value = dtwinOperationModelsResults.value || [];
    emit("allOperationModelsLoaded", allOperationModels.value);
  }

  isLoadingAllOperationModels.value = false;
}

const isOperationModelTypeFirmUpdateSelected: ComputedRef<boolean> = computed(
  () => form.value.operationModelType === OperationModelType.FIRM_UPDATE,
);

function onSelectFirmware(firmware?: Firmware) {
  form.value.firmwareUuid = firmware?.uuid;
  emit("selectFirmware", firmware);
}

// Campaign settings rules
const rules = {
  name: [
    {
      required: true,
      message: t("campaign.funnel.campaignSettings.validation.name"),
      whitespace: true,
      trigger: "blur",
    },
    {
      max: 100,
      message: t("input.error.maxLength", { maxLength: 100 }),
      whitespace: true,
      trigger: "blur",
    },
  ],
  operationModelType: [
    {
      required: true,
      message: t("campaign.funnel.campaignSettings.validation.type"),
      trigger: "change",
    },
  ],
};

const formRef: Ref<typeof LxcForm | undefined> = ref();
const campaignFunnelStep1SelectFirmwareRef: Ref<
  typeof CampaignOperationMgrFunnelStep1SelectFirmware | undefined
> = ref();

const form: ComputedRef<CampaignFunnelStep1> = computed({
  get() {
    return props.modelValue;
  },
  set(form: CampaignFunnelStep1) {
    emit("update:modelValue", form);
  },
});

function onClickOperationModelType(operationModelType: OperationModelType) {
  if (props.canEdit) {
    form.value.operationModelType =
      form.value.operationModelType != operationModelType
        ? (form.value.operationModelType = operationModelType)
        : undefined;
  }
}

// reset the selected firmware when changing operation model type
watch(
  () => form.value.operationModelType,
  (operationModelType) => {
    if (operationModelType !== OperationModelType.FIRM_UPDATE) {
      onSelectFirmware(undefined);
    }
  },
);

onMounted(setAllOperationModels);

defineExpose({
  validate: async () =>
    Promise.resolve(
      (await formRef.value?.validate().catch(() => false)) & // single "&" in order to execute all the validate functions
        (!isOperationModelTypeFirmUpdateSelected.value ||
          (await campaignFunnelStep1SelectFirmwareRef.value
            ?.validate()
            .catch(() => false))),
    ),
});
</script>

<template>
  <lxc-form ref="formRef" :model="form" :rules="rules" @submit.prevent>
    <h4 class="mt-0 mb-8 text-gray-900">
      {{ t("campaign.funnel.campaignSettings.title") }}
    </h4>

    <lxc-form-item
      :label="t('campaign.funnel.campaignSettings.name')"
      class="w-80 mb-4"
      prop="name"
    >
      <lxc-input
        v-model="form.name"
        type="text"
        :placeholder="t('campaign.funnel.campaignSettings.name')"
        :disabled="isEditing"
      />
    </lxc-form-item>

    <lxc-form-item
      :label="t('campaign.funnel.campaignSettings.typeOfCampaign.title')"
      display-label
      prop="operationModelType"
    >
      <container-component
        :is-loading="isLoadingAllOperationModels"
        :error="errorAllOperationModels"
        class="!p-0"
      >
        <div class="flex gap-6">
          <div
            v-for="operationModelType of distinctOperationModelTypes"
            :key="operationModelType"
            :class="`${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'} flex items-center min-w-[12.688rem] py-[0.937rem] rounded-xl px-4 ${form.operationModelType === operationModelType ? 'bg-primary-50 border-2 border-primary-800' : 'bg-white border border-gray-300'}`"
            @click="onClickOperationModelType(operationModelType)"
          >
            <!-- build label specifically in order to manage the color and set the checkbox label to empty string in order to not display it -->
            <lxc-checkbox
              v-model="form.operationModelType"
              :value="operationModelType"
              label=""
              :disabled="!canEdit"
            />
            <label
              :class="`${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'} ml-2 ${form.operationModelType === operationModelType ? 'text-primary-800' : ''}`"
            >
              {{ getOperationModelTypeLabel(operationModelType, te, t) }}
            </label>
          </div>
        </div>
      </container-component>
    </lxc-form-item>
  </lxc-form>

  <div v-if="isOperationModelTypeFirmUpdateSelected">
    <campaign-operation-mgr-funnel-step1-select-firmware
      ref="campaignFunnelStep1SelectFirmwareRef"
      v-model="form.firmwareUuid"
      :can-edit="canEdit"
      :operation-models="allOperationModels"
      @select-firmware="onSelectFirmware"
    />
  </div>
</template>
